import { InlineAlert } from '../../components/BaseComponents'
import { useEffect, useState } from "react";
import { Button, LoadingSpinner } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import OrganisationJurisdictionSelect from "../../components/BaseComponents/OrganisationJurisdictionSelect";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type EditTrainingOrganisationModalProps = {
	trainingSessionId: string;
	organisation: any;
	onClose: () => void;
	onUpdated: () => void;
	isOpen?: boolean;
};

// Sub-task I-extra2 §4.2/§5.2 (docs/todo/user-org-membership-i-extra2-training-org-reassignment.md):
// the only UI allowed to rename/re-jurisdiction a training org - a deliberately separate action
// from reassigning which trainees belong to it (EditTrainingUserModal).
function EditTrainingOrganisationModal({ trainingSessionId, organisation, onClose, onUpdated, isOpen }: EditTrainingOrganisationModalProps) {
	const [name, setName] = useState('');
	const [jurisdictionCode, setJurisdictionCode] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const api = useApi();

	useEffect(() => {
		if (isOpen && organisation) {
			setName(organisation.name || '');
			setJurisdictionCode(organisation.jurisdictionCode);
			setError('');
		}
	}, [isOpen, organisation]);

	const update = async () => {
		setLoading(true);
		setError('');
		try {
			await api.put(`/admin/training-sessions/${trainingSessionId}/organisations/${organisation.id}`, {
				name,
				jurisdictionCode,
				_csrf: await getCSRFToken(),
			})
			onUpdated();
		} catch (err: any) {
			setError(getApiErrorMessage(err, 'Failed to update this organisation.'));
		} finally {
			setLoading(false);
		}
	}

	return (
		<CanonicalModal
			open={!!isOpen}
			onClose={onClose}
			size="small"
			title="Edit organisation"
			actions={
				<>
					<Button
						onClick={update}
						additionalClass="mr-5"
						disabled={!name || loading}
						type="Primary"
						text="Update organisation"
					/>
					<Button onClick={onClose} type="Outlined" text="Cancel" />
				</>
			}
		>
			<Input
				label="Organisation name"
				name="name"
				additionalClass="mt-8"
				value={name}
				onChange={e => setName(e.target.value)}
				type="text"
				placeholder="Organisation name..."
			/>
			<OrganisationJurisdictionSelect
				value={jurisdictionCode}
				onChange={setJurisdictionCode}
				label="Jurisdiction"
				isClearable={false}
				fillContainer
			/>
			{ loading && <LoadingSpinner /> }
			{ error && (
				<InlineAlert type='error' className='mt-4'>{ error }
				</InlineAlert>
			)}
		</CanonicalModal>
	)
}

export default EditTrainingOrganisationModal
